/**
 * Access-gated routes. `pagesFor` resolves pages from the caller's own access (`GET /api/v1/me`),
 * never from IdP group names.
 */

import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Spinner from "@cloudscape-design/components/spinner"
import { Navigate } from "react-router-dom"
import type { Page } from "../../utils/access"
import { useMyAccess } from "../../hooks/queries/useMyAccess"
import { ROUTES } from "../../utils/constants"
import { AccessNotConfigured } from "../SignInMessage/SignInMessage"

const Loading = () => (
  <Box textAlign="center" padding="l">
    <Spinner size="large" nativeAttributes={{ role: "progressbar", "aria-label": "Loading" }} />
  </Box>
)

const AccessError = () => (
  <Box padding="l">
    <Alert type="error" header="Could not load your access">
      Refresh the page to try again, or contact your administrator if the problem continues.
    </Alert>
  </Box>
)

interface AccessRouteProps {
  children: React.ReactNode
  /**
   * The route's page. Omit for a route shared by every caller with any page.
   */
  page?: Page
}

/**
 * Render `children` when the caller can see the route's page, otherwise redirect to
 * {@link HomeRedirect}.
 */
export const AccessRoute = ({ children, page }: AccessRouteProps) => {
  const access = useMyAccess()

  if (access.isLoading) return <Loading />
  if (access.error) return <AccessError />

  const allowed =
    page === undefined ? access.pages.length > 0 : access.pages.some((p) => p.page === page)

  return allowed ? <>{children}</> : <Navigate to={ROUTES.HOME} replace />
}

/**
 * Send the caller to their first page, or show the "access not configured" message in place when
 * they have none.
 */
export const HomeRedirect = () => {
  const access = useMyAccess()

  if (access.isLoading) return <Loading />
  if (access.error) return <AccessError />
  const [landing] = access.pages
  if (landing === undefined) {
    return <AccessNotConfigured />
  }
  return <Navigate to={landing.href} replace />
}
